import React from 'react';
import { useAppSelector } from '../../../store/hooks';
import { Document, FilingType } from '../../../store/document/types';
import styles from './EightKList.module.scss';
import { Button } from '../../UI';

interface EightKListProps {
  companyDocuments: Document[];
  onDocumentClick: (docId: string) => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const format8KName = (document: Document) => {
  const items = document.metadata?.items;
  if (Array.isArray(items) && items.length > 0) {
    return `8-K - ${items.join(', ')}`;
  }
  return '8-K';
};

const formatExhibitName = (document: Document) => {
  const exhibitNumber = document.metadata?.exhibitNumber;
  return exhibitNumber ? `Exhibit ${exhibitNumber}` : 'Exhibit';
};

const formatEarningsReleaseName = () => 'Earnings Release';

const EightKList: React.FC<EightKListProps> = ({ companyDocuments, onDocumentClick }) => {
  const documentId = useAppSelector((state) => state.document.documentId);

  // Filter for 8-Ks, exhibits, and earnings releases
  const filteredDocs = companyDocuments.filter(
    (doc) =>
      doc.type === FilingType.EIGHT_K ||
      doc.type === FilingType.EXHIBIT ||
      doc.type === FilingType.EARNINGS_RELEASE,
  );

  // Group by 8-K accessNumber
  const grouped: Record<string, { eightK: Document; exhibits: Document[]; earnings: Document[] }> =
    {};
  for (const doc of filteredDocs) {
    const accessNumber = doc.metadata?.accessNumber;
    if (!accessNumber) continue;
    if (doc.type === FilingType.EIGHT_K) {
      if (!grouped[accessNumber])
        grouped[accessNumber] = { eightK: doc, exhibits: [], earnings: [] };
      grouped[accessNumber].eightK = doc;
    } else if (doc.type === FilingType.EXHIBIT) {
      if (!grouped[accessNumber])
        grouped[accessNumber] = { eightK: undefined as any, exhibits: [], earnings: [] };
      grouped[accessNumber].exhibits.push(doc);
    } else if (doc.type === FilingType.EARNINGS_RELEASE) {
      if (!grouped[accessNumber])
        grouped[accessNumber] = { eightK: undefined as any, exhibits: [], earnings: [] };
      grouped[accessNumber].earnings.push(doc);
    }
  }

  // Sort 8-Ks by published_at (most recent first)
  const sortedGroups = Object.values(grouped)
    .filter((g) => g.eightK)
    .sort((a, b) => {
      const dateA = a.eightK.published_at ? new Date(a.eightK.published_at).getTime() : 0;
      const dateB = b.eightK.published_at ? new Date(b.eightK.published_at).getTime() : 0;
      return dateB - dateA;
    });

  if (sortedGroups.length === 0) {
    return <div className={styles.emptyDocuments}>No 8-K filings found.</div>;
  }

  return (
    <div>
      {sortedGroups.map(({ eightK, exhibits, earnings }) => (
        <div key={eightK.id} className={styles.eightKGroup}>
          <Button
            variant={documentId === eightK.id ? 'primary' : 'ghost'}
            size="medium"
            fullWidth={false}
            onClick={() => onDocumentClick(eightK.id)}
            className={styles.eightKButton + (documentId === eightK.id ? ' ' + styles.active : '')}
          >
            {format8KName(eightK)}
          </Button>
          <div className={styles.children}>
            {earnings.map((doc) => (
              <Button
                key={doc.id}
                variant={documentId === doc.id ? 'primary' : 'ghost'}
                size="small"
                fullWidth={false}
                onClick={() => onDocumentClick(doc.id)}
                className={styles.childButton + (documentId === doc.id ? ' ' + styles.active : '')}
              >
                {formatEarningsReleaseName()}
              </Button>
            ))}
            {exhibits.map((doc) => (
              <Button
                key={doc.id}
                variant={documentId === doc.id ? 'primary' : 'ghost'}
                size="small"
                fullWidth={false}
                onClick={() => onDocumentClick(doc.id)}
                className={styles.childButton + (documentId === doc.id ? ' ' + styles.active : '')}
              >
                {formatExhibitName(doc)}
              </Button>
            ))}
          </div>
          <div className={styles.date}>
            {eightK.published_at ? formatDate(eightK.published_at) : ''}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EightKList;
